<template>
  <main class="supplier-workbench" :style="{height:workbenchHeight ? workbenchHeight+'px' : null}">
    <div class="wb-content">
      <el-alert v-if="sessionError" :title="sessionError" type="error" :closable="false" show-icon />

      <!-- 协同单据统计 -->
      <section class="wb-metrics" :aria-label="$LANG('', '协同概览')">
        <!-- 待确认 -->
        <div class="wb-metric wb-metric--pending">
          <div class="wb-metric-row">
            <span class="wb-metric-title">
              <span class="wb-status-dot" />
              <span>{{ $LANG('', '待确认订单') }}</span>
            </span>
            <div class="wb-metric-data">
              <strong>{{ metricsReady ? metrics.pending : '—' }}</strong>
            </div>
          </div>
        </div>

        <!-- 已确认 -->
        <div class="wb-metric wb-metric--confirmed">
          <div class="wb-metric-row">
            <span class="wb-metric-title">
              <span class="wb-status-dot" />
              <span>{{ $LANG('', '已确认订单') }}</span>
            </span>
            <div class="wb-metric-data">
              <strong>{{ metricsReady ? metrics.confirmed : '—' }}</strong>
            </div>
          </div>
        </div>

        <!-- 已开送货单 -->
        <div class="wb-metric wb-metric--delivery">
          <div class="wb-metric-row">
            <span class="wb-metric-title">
              <span class="wb-status-dot" aria-hidden="true" />
              <span>{{ $LANG('', '已开送货单') }}</span>
            </span>
            <div class="wb-metric-data">
              <strong>{{ metricsReady ? metrics.deliveries : '—' }}</strong>
            </div>
          </div>
        </div>

      </section>

      <!-- 主工作区：微凸 Tab 栏与数据视图容器 -->
      <section class="wb-workspace">
        <nav class="wb-tabs" role="tablist" :aria-label="$LANG('', '业务视图')">
          <div class="wb-tabs-group">
            <button
              v-for="(tab, index) in tabs"
              :id="'wb-tab-'+tab.key"
              :key="tab.key"
              role="tab"
              :aria-selected="activeTab===tab.key ? 'true':'false'"
              :aria-controls="'wb-panel-'+tab.key"
              :class="{active: activeTab===tab.key, 'is-first': index === 0}"
              @click="switchTab(tab.key)"
            >
              <i :class="tab.icon" aria-hidden="true" />
              <span>{{ tab.label }}</span>
              <span v-if="tab.key==='orders' && metricsReady" class="wb-tab-count wb-tab-count--pending font-mono">
                {{ metrics.pending }}
              </span>
              <span v-if="tab.key==='deliveries' && metricsReady" class="wb-tab-count font-mono">
                {{ metrics.deliveries }}
              </span>
            </button>
          </div>
        </nav>

        <div class="wb-panel-heading"><h2>{{ activeTitle }}</h2></div>
        <div ref="panelHost" class="wb-panel-body">
          <section v-if="visited.orders && !sessionError" id="wb-panel-orders" v-show="activeTab==='orders'" role="tabpanel" aria-labelledby="wb-tab-orders">
            <WorkbenchTable ref="orders" kind="orders" :api="service" :context="session" :active="pageActive && activeTab==='orders'" :body-height="bodyHeight" @loaded="onLoaded" @failed="markDirty" @changed="refreshAll" />
          </section>
          <section v-if="visited.deliveries && !sessionError" id="wb-panel-deliveries" v-show="activeTab==='deliveries'" role="tabpanel" aria-labelledby="wb-tab-deliveries">
            <WorkbenchTable ref="deliveries" kind="deliveries" :api="service" :context="session" :active="pageActive && activeTab==='deliveries'" :body-height="bodyHeight" @loaded="onLoaded" @failed="markDirty" @changed="refreshAll" />
          </section>
          <section v-if="visited.ledger && !sessionError" id="wb-panel-ledger" v-show="activeTab==='ledger'" role="tabpanel" aria-labelledby="wb-tab-ledger">
            <WorkbenchTable ref="ledger" kind="ledger" :api="service" :context="session" :active="pageActive && activeTab==='ledger'" :body-height="bodyHeight" @loaded="onLoaded" @failed="markDirty" />
          </section>
        </div>
      </section>
    </div>


  </main>
</template>
<script>
import ResizeObserver from 'resize-observer-polyfill'
import WorkbenchTable from './WorkbenchTable.vue'

// Read permissions for all tabs; only the KPI cards query data here.
const PANEL_DEFS = Object.freeze({
  orders: { form: '397191', table: 'V_po_ord', date: 'ORDDTTM' },
  deliveries: { form: '401217', table: 'V_po_sent', date: 'SHPDTTM' },
  ledger: { form: '397193' }
})

function requireContext(context) {
  if (!context || !String(context.corpId || '').trim() || !String(context.userNo || '').trim()) throw new Error('会话信息不完整，请重新登录')
  return context
}
function buildMetricsCondition(kind, context) {
  const user = String(context.userNo).replace(/'/g, "''")
  // Match the native supplier scope across all dates. Table filters stay in each Section.
  const condition = kind === 'orders'
    ? ` and POTY='PRD' and stfg in ('K2','Y') and ordstfg!='END' and paysrc!='433336' and supno in (select empidseq from v_sys_user where userno='${user}')`
    : ` and addemp='${user}'`
  return condition + ` order by ${PANEL_DEFS[kind].date} desc`
}
function summarizeLoaded(orders, deliveries) {
  const unique = rows => [...new Map(rows.filter(row => row.IDSEQ != null).map(row => [String(row.IDSEQ), row])).values()]
  const rows = unique(orders)
  return { pending: rows.filter(row => row.ISCHK === 'N').length, confirmed: rows.filter(row => row.ISCHK === 'Y').length, deliveries: unique(deliveries).length }
}
function rowsFrom(response) {
  if (!response || String(response.code) !== '200') {
    const error = new Error(String(response && (response.result || response.message) || '查询失败'))
    error.authorization = !!response && ['401','403'].includes(String(response.code))
    throw error
  }
  if (!Array.isArray(response.result)) throw new Error('接口数据格式不符合契约')
  return response.result
}
// Native Sections own business queries, mutations and reports.
function createWorkbenchService(request, context) {
  const query = async body => {
    requireContext(context)
    try { return rowsFrom(await request.post('xuper_auth/getBaseData', body)) } catch (error) {
      if (error.response && [401,403].includes(Number(error.response.status))) error.authorization = true
      throw error
    }
  }
  const permission = async kind => {
    const def = PANEL_DEFS[kind]
    if (!def) throw new Error('业务视图无效')
    let rows
    try { rows = await query({ p_key: 'userno', p_value: context.userNo, p_key2: 'frmidseq', p_value2: def.form, p_table: 'v_sys_user_priv', p_cond: " and issel='Y'" }) } catch (error) { error.authorization = true; throw error }
    const granted = rows.find(row => row.ISSEL === 'Y')
    if (!granted) throw Object.assign(new Error('没有此业务的查询权限'), { authorization: true })
    return granted
  }
  return {
    permission,
    async list(kind) {
      const def = PANEL_DEFS[kind]
      if (!def || !def.table) throw new Error('统计视图无效')
      const granted = await permission(kind)
      const rows = await query({ p_key: 'corpid', p_value: context.corpId, p_table: def.table, p_cond: buildMetricsCondition(kind, context) })
      return { rows, permission: granted }
    }
  }
}

export default {
  name: 'SupplierWorkbench',
  components: { WorkbenchTable },
  props: { api: { type: Object, default: null }, context: { type: Object, default: null } },
  data() {
    const session = this.context || { userNo: this.$cookies.get('v_telno'), corpId: this.$cookies.get('v_corpid'), admin: this.$cookies.get('v_admin') }
    let sessionError = ''
    try { requireContext(session) } catch (_) { sessionError = this.$LANG('', '会话信息不完整，请重新登录') }
    return { session, sessionError, service: this.api || createWorkbenchService(this.$axios, session), activeTab: 'orders', visited: { orders: true, deliveries: false, ledger: false }, dirty: { orders: false, deliveries: false, ledger: false }, pageActive: true, workbenchHeight: 0, bodyHeight: 400, metrics: { pending: 0, confirmed: 0, deliveries: 0 }, metricsReady: false, metricEpoch: 0 }
  },
  computed: {
    tabs() { return [{ key: 'orders', label: '采购订单', icon: 'el-icon-document' }, { key: 'deliveries', label: '采购送货', icon: 'el-icon-truck' }, { key: 'ledger', label: '采购对账', icon: 'el-icon-tickets' }] },
    activeTitle() { return this.tabs.find(tab => tab.key === this.activeTab).label }
  },
  mounted() { this.startObserver(); this.refreshMetrics() },
  activated() { if (!this.isCurrentSession()) { this.suspend(); return } this.pageActive = true; this.startObserver(); this.refreshMetrics(); if (this.dirty[this.activeTab]) this.$nextTick(() => this.refreshPanel(this.activeTab, true)) },
  deactivated() { this.suspend() },
  beforeDestroy() { this.suspend(); this.service = null },
  methods: {
    isCurrentSession() {
      if (this.context) return true
      return !!this.$cookies && String(this.$cookies.get('v_telno') || '') === String(this.session.userNo || '') && String(this.$cookies.get('v_corpid') || '') === String(this.session.corpId || '')
    },
    startObserver() {
      if (this._panelObserver || !this.$refs.panelHost) return
      this._panelObserver = new ResizeObserver(() => this.scheduleMeasure())
      this._panelObserver.observe(this.$refs.panelHost)
      if (this.$el && this.$el.parentElement) this._panelObserver.observe(this.$el.parentElement)
      window.addEventListener('resize', this.scheduleMeasure)
      this.scheduleMeasure()
    },
    scheduleMeasure() {
      clearTimeout(this._measureTimer)
      this._measureTimer = setTimeout(() => this.$nextTick(() => {
        cancelAnimationFrame(this._measureFrame)
        this._measureFrame = requestAnimationFrame(() => {
          this.measureViewport()
        })
      }), 80)
    },
    measureViewport() {
      if (!this.pageActive || !this.$el || !this.$refs.panelHost) return
      const top = Math.max(0, this.$el.getBoundingClientRect().top)
      this.workbenchHeight = Math.max(0, Math.floor(window.innerHeight - top))
      const panelHeight = Math.floor(this.$refs.panelHost.getBoundingClientRect().height)
      if (panelHeight > 0) this.bodyHeight = panelHeight
    },
    suspend() {
      this.pageActive = false; this.metricEpoch++
      clearTimeout(this._measureTimer); cancelAnimationFrame(this._measureFrame)
      if (this._panelObserver) this._panelObserver.disconnect()
      this._panelObserver = null
      window.removeEventListener('resize', this.scheduleMeasure)
      Object.keys(this.dirty).forEach(key => { this.dirty[key] = true })
    },
    switchTab(key) { this.activeTab = key; this.visited[key] = true; this.scheduleMeasure(); if (this.dirty[key]) this.$nextTick(() => this.refreshPanel(key, true)) },
    onLoaded({ kind }) { if (!this.pageActive || kind !== this.activeTab) { this.dirty[kind] = true; return } this.dirty[kind] = false; this.refreshMetrics() },
    markDirty(kind) { this.dirty[kind] = true },
    async refreshPanel(key, skipLoading = false) { const panel = this.$refs[key]; const table = panel && (panel.$refs.embeddedTable || panel); if (skipLoading && table && table.loading) return; if (panel && key === this.activeTab && this.pageActive) { this.dirty[key] = true; const ok = await panel.refresh(); if (!ok) this.dirty[key] = true } },
    refreshAll() { Object.keys(this.dirty).forEach(key => { this.dirty[key] = true }); this.refreshPanel(this.activeTab); this.refreshMetrics() },
    async refreshMetrics() {
      if (this.sessionError || !this.pageActive || !this.isCurrentSession()) return
      const epoch = ++this.metricEpoch
      try {
        const [orders, deliveries] = await Promise.all([this.service.list('orders'), this.service.list('deliveries')])
        if (epoch !== this.metricEpoch) return
        this.metrics = summarizeLoaded(orders.rows, deliveries.rows); this.metricsReady = true
      } catch (_) { if (epoch === this.metricEpoch) this.metricsReady = false }
    }
  }
}
</script>
<style scoped>
.supplier-workbench {
  --wb-brand: #2563eb;
  --wb-brand-hover: #1d4ed8;
  --wb-brand-soft: #eff6ff;
  --wb-border: rgba(226, 232, 240, 0.85);
  --wb-shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  --wb-shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.03);
  --wb-shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.04), 0 2px 4px -2px rgba(0, 0, 0, 0.03);
  --wb-mono: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  box-sizing: border-box; display: flex; flex-direction: column; height: calc(100vh - 80px); min-height: 0; overflow: hidden; background: #f8fafc; color: #1e293b;
  font: 13px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  -webkit-font-smoothing: antialiased;
}
.supplier-workbench *, .supplier-workbench *::before, .supplier-workbench *::after { box-sizing: border-box; }
.font-mono { font-family: var(--wb-mono) !important; }

/* 页面核心容器 */
.wb-content {
  display: flex; flex-direction: column; flex: 1; gap: 8px;
  width: 100%; max-width: none; min-height: 0; margin: 0; padding: 4px 14px 10px;
}

/* 三列单据统计 */
.wb-metrics {
  flex-shrink: 0; display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px; user-select: none;
}
.wb-metric {
  width: 100%; text-align: left; padding: 8px 14px;
  border: 1px solid var(--wb-border); border-radius: 8px;
  background: #fff; box-shadow: var(--wb-shadow-sm);
  font: inherit; color: inherit;
  display: flex; align-items: center; min-height: 46px;
}
.wb-metric-row {
  display: flex; align-items: center; justify-content: space-between;
  width: 100%; gap: 8px;
}
.wb-metric-title {
  display: flex; align-items: center; gap: 6px;
  color: #1e293b; font-size: 13px; font-weight: 700; white-space: nowrap; flex-shrink: 0;
}
.wb-status-dot { display: inline-block; flex-shrink: 0; width: 7px; height: 7px; border-radius: 50%; background: #94a3b8; }
.wb-metric--pending .wb-status-dot { background: #f59e0b; box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2); }
.wb-metric--confirmed .wb-status-dot { background: #10b981; box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2); }
.wb-metric--delivery .wb-status-dot { background: #2563eb; box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2); }
.wb-metric-data {
  display: flex; align-items: baseline; gap: 6px;
  white-space: nowrap; overflow: hidden;
}
.wb-metric-data strong {
  font-family: var(--wb-mono); font-weight: 800; font-size: 19px; line-height: 1;
  letter-spacing: -.4px; color: #0f172a;
}
/* 主工作台容器 */
.wb-workspace {
  display: flex; flex-direction: column; flex: 1; min-height: 0;
  background: #fff; border: 1px solid var(--wb-border); border-radius: 12px;
  box-shadow: var(--wb-shadow-sm); overflow: hidden;
}

/* 微凸顶卡片式 Tab 栏 (完全去除上下左右冗余 padding，与卡片外框 0 间隙无缝咬合) */
.wb-tabs {
  flex-shrink: 0; display: flex; align-items: flex-end; justify-content: space-between;
  padding: 0 12px 0 0; /* 上部和左侧完全 0 padding，消除任何空隙与内层条带感 */
  background: rgba(248, 250, 252, 0.65);
  border-bottom: 1px solid var(--wb-border);
  user-select: none;
}
.wb-tabs-group { display: flex; align-items: flex-end; gap: 0; }
.wb-tabs-group>button {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 16px 8px; margin-bottom: -1px;
  border: 1px solid transparent; border-bottom: 1px solid transparent;
  border-top: none; /* 顶部完全对齐卡片顶边缘 */
  border-radius: 0;
  font-size: 12px; line-height: 18px; font-weight: 500; font-family: inherit;
  color: #64748b; background: transparent; white-space: nowrap; cursor: pointer;
  transition: all .15s ease;
}
.wb-tabs-group>button:hover { color: #0f172a; background: rgba(241, 245, 249, 0.7); }
.wb-tabs-group>button.active {
  position: relative; background: #fff; color: var(--wb-brand);
  border-color: rgba(226, 232, 240, 0.9); border-bottom-color: #fff;
  border-top: 2px solid var(--wb-brand); /* 顶部贴边专属主色指示线，无上部缝隙 */
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.02); font-weight: 600;
}
.wb-tabs-group>button.is-first.active {
  border-left-color: transparent;
  border-top-left-radius: 11px;
}
.wb-tabs-group>button>i { font-size: 13px; }

/* 药丸计数徽标 */
.wb-tab-count {
  padding: 1px 6px; border-radius: 999px;
  background: #f1f5f9; border: 1px solid #e2e8f0; color: #64748b;
  font-size: 10px; font-weight: 700; line-height: 14px;
}
.wb-tab-count--pending { color: #b45309; background: #fffbeb; border-color: #fde68a; }

.wb-panel-heading { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; }
.wb-panel-body { display: flex; flex-direction: column; flex: 1; min-height: 0; min-width: 0; }
.wb-panel-body>section { display: flex; flex-direction: column; flex: 1; height: 100%; min-height: 0; }

.wb-tabs-group>button:focus-visible { outline: 2px solid var(--wb-brand); outline-offset: 3px; }

@media(max-width:760px) {
  .wb-metrics { grid-template-columns: repeat(2, minmax(0,1fr)); }
}
@media(max-width:900px) {
  .wb-content { padding: 10px 12px; }
  .wb-tabs { overflow-x: auto; }
}
@media(max-width:560px) {
  .wb-metrics { grid-template-columns: 1fr; gap: 10px; }
  .wb-metric { padding: 13px; }
}
@media(prefers-reduced-motion:reduce) { .wb-tabs-group>button { transition: none; } }
</style>
